import React, { useRef, useState } from 'react';
import {
  Box,
  HStack,
  IconButton,
  Text,
  Textarea,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';
import { SendHorizonal } from 'lucide-react';
import { sendWAMsg } from '../../../hooks/whatsapp';
import { WhatsappMessage } from '../../../utils/types';

interface ComposerProps {
  conversationId: string;
  orgId: string;
  recipient_phone: string;
  onSent?: (message: WhatsappMessage) => void;
  isWindowExpired?: boolean;
}

export default function Composer({
  conversationId,
  orgId,
  onSent,
  isWindowExpired = false,
}: ComposerProps) {
  const [text, setText] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();

  const send = async () => {
    if (isWindowExpired) {
      toast({
        title: 'Free-text messaging is unavailable',
        description: 'The 24-hour customer care window has expired for this chat.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    const trimmed = text.trim();
    if (!trimmed && !attachment) {
      return;
    }

    setIsSending(true);
    try {
      const response = await sendWAMsg(conversationId, orgId, trimmed, undefined, attachment);

      if (response.success && response.data) {
        if (onSent) {
          onSent(response.data as WhatsappMessage);
        }
        setText('');
        setAttachment(null);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      } else if (response.data?.status === 'failed') {
        if (onSent) {
          onSent(response.data as WhatsappMessage);
        }
        toast({
          title: 'Message was not sent',
          description:
            response.data?.raw_payload?.detail ||
            'The message failed backend validation.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Failed to send message',
          description:
            response.data?.detail || 'Something went wrong while sending this message.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Failed to send message',
        description: error?.response?.data?.detail || 'Something went wrong.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <VStack align="stretch" spacing={2}>
      {isWindowExpired ? (
        <Box
          bg="orange.50"
          color="orange.800"
          borderWidth="1px"
          borderColor="orange.200"
          borderRadius="14px"
          px={3}
          py={2.5}
        >
          <Text fontSize="sm" fontWeight="700">
            The 24-hour WhatsApp free-text window has expired.
          </Text>
          <Text fontSize="sm">
            To avoid Cloud API policy violations, free-text replies are disabled for this chat until the customer messages again or an approved template workflow is available.
          </Text>
        </Box>
      ) : null}

      <HStack spacing={2} align="flex-end">
        <Tooltip label={isWindowExpired ? 'Attachments are disabled outside the 24-hour window' : 'Send an image, video, audio file, or document'}>
          <IconButton
            aria-label="Attach file"
            icon={<AttachmentIcon />}
            variant="ghost"
            colorScheme="gray"
            borderRadius="full"
            isDisabled={isWindowExpired || isSending}
            onClick={() => inputRef.current?.click()}
          />
        </Tooltip>
        <input
          ref={inputRef}
          type="file"
          style={{ display: 'none' }}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          onChange={(event) => setAttachment(event.target.files?.[0] || null)}
        />

        <Textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={isWindowExpired ? 'Free-text messaging is disabled for this conversation' : 'Type a message'}
          resize="none"
          minH="44px"
          maxH="140px"
          rows={1}
          borderRadius="22px"
          bg="white"
          borderColor="gray.300"
          _focusVisible={{
            borderColor: 'teal.400',
            boxShadow: '0 0 0 1px rgba(18, 140, 126, 0.22)',
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              send();
            }
          }}
          isDisabled={isWindowExpired || isSending}
        />

        <IconButton
          onClick={send}
          aria-label="Send message"
          icon={<SendHorizonal size={18} />}
          isLoading={isSending}
          isDisabled={(!text.trim() && !attachment) || isWindowExpired}
          bg="#128C7E"
          color="white"
          _hover={{ bg: '#0F766E' }}
          _active={{ bg: '#115E59' }}
          borderRadius="full"
        />
      </HStack>
      {attachment ? (
        <Box
          bg="gray.50"
          color="gray.700"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="14px"
          px={3}
          py={2}
        >
          <Text fontSize="sm" fontWeight="600">
            Attachment ready
          </Text>
          <Text fontSize="sm">{attachment.name}</Text>
        </Box>
      ) : null}
    </VStack>
  );
}
